import { useErrorHandler } from '@shared/errors';
import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';

import { makeDrugQueryKey } from '@/features/drugs/api/use-drug';
import { useDrugPackagingsService } from '@/features/drugs/api/use-drug-packagings-service';
import { CoverageTag } from '@/features/drugs/types/coverage-tag';
import { Drug } from '@/features/drugs/types/drug';
import { notifySuccess } from '@/lib/notification/notifications';

type PackagingCoverageTagVariables = {
  drugId: number;
  ndc: string;
  coverageTag: CoverageTag;
};

const withTag = (tags: CoverageTag[], tag: CoverageTag) => (
  [...tags.filter(it => it.id !== tag.id), tag].sort((a, b) => a.name.localeCompare(b.name))
);

const withoutTag = (tags: CoverageTag[], tag: CoverageTag) => tags.filter(it => it.id !== tag.id);

const patchPackagingTags = (
  queryClient: QueryClient,
  { drugId, ndc, coverageTag }: PackagingCoverageTagVariables,
  patch: (tags: CoverageTag[], tag: CoverageTag) => CoverageTag[]
) => {
  queryClient.setQueryData<Drug>(makeDrugQueryKey(drugId), (drug) => drug && {
    ...drug,
    packagings: drug.packagings.map(packaging => (
      packaging.ndc === ndc ? { ...packaging, coverageTags: patch(packaging.coverageTags, coverageTag) } : packaging
    ))
  });
};

// The PUT/DELETE return no body, and the optimistic patch is exactly what the server would send back,
// so a successful mutation doesn't refetch the (expensive) drug details. Rollback is an inverse patch
// rather than a snapshot restore so it can't clobber another cell's concurrent change.
export const useAddPackagingCoverageTag = () => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();
  const { addCoverageTagToPackaging } = useDrugPackagingsService();

  return useMutation({
    mutationFn: ({ drugId, ndc, coverageTag }: PackagingCoverageTagVariables) => (
      addCoverageTagToPackaging(drugId, ndc, coverageTag.id)
    ),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: makeDrugQueryKey(variables.drugId) });
      patchPackagingTags(queryClient, variables, withTag);
    },
    onSuccess: (_, { ndc, coverageTag }) => {
      notifySuccess({ title: 'Saved', message: `Added Coverage Tag ${coverageTag.name} to ${ndc}.` });
    },
    onError: async (error, variables) => {
      patchPackagingTags(queryClient, variables, withoutTag);
      handleError(error, { title: `Failed to add Coverage Tag ${variables.coverageTag.name} to ${variables.ndc}` });
      await queryClient.invalidateQueries({ queryKey: makeDrugQueryKey(variables.drugId) });
    }
  });
};

export const useRemovePackagingCoverageTag = () => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();
  const { removeCoverageTagFromPackaging } = useDrugPackagingsService();

  return useMutation({
    mutationFn: ({ drugId, ndc, coverageTag }: PackagingCoverageTagVariables) => (
      removeCoverageTagFromPackaging(drugId, ndc, coverageTag.id)
    ),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: makeDrugQueryKey(variables.drugId) });
      patchPackagingTags(queryClient, variables, withoutTag);
    },
    onSuccess: (_, { ndc, coverageTag }) => {
      notifySuccess({ title: 'Saved', message: `Removed Coverage Tag ${coverageTag.name} from ${ndc}.` });
    },
    onError: async (error, variables) => {
      patchPackagingTags(queryClient, variables, withTag);
      handleError(error, { title: `Failed to remove Coverage Tag ${variables.coverageTag.name} from ${variables.ndc}` });
      await queryClient.invalidateQueries({ queryKey: makeDrugQueryKey(variables.drugId) });
    }
  });
};
